<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { supabase, useSupabase } from "../db/supabaseClient.js";
import { currentUser } from "../db/auth.js";
import {
  addToCart,
  cartState,
  cartTotal,
  clearCart,
  removeFromCart,
  setItemQty,
} from "../store/cart.js";
import chefImg from "../assets/cocinero.png";

function formatMoney(n) {
  try {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(Number(n || 0));
  } catch {
    return `$ ${Number(n || 0).toFixed(2)}`;
  }
}
function finalPrice(unit, discount, taxRate) {
  const u = Number(unit || 0);
  const d = Number(discount || 0);
  const base = d > 0 ? u * (1 - d / 100) : u;
  const t = Number(taxRate || 0);
  return Number((base * (1 + t / 100)).toFixed(2));
}
async function buildThumbs(items) {
  const ids = [];
  const out = [];
  for (const it of items || []) {
    if (it.product_id) ids.push(it.product_id);
  }
  if (ids.length) {
    const { data: det } = await supabase
      .from("v_inventory_basic")
      .select("id,name,unit_price,discount,image_base64,image_mime,tax_rate")
      .in("id", ids);
    const map = new Map((det || []).map((d) => [d.id, d]));
    for (const it of items) {
      const d = map.get(it.product_id);
      if (!d) continue;
      out.push({
        name: d.name,
        qty: Number(it.quantity || 1),
        price: finalPrice(d.unit_price, d.discount, d.tax_rate),
        image_base64: d.image_base64,
        image_mime: d.image_mime,
      });
    }
  }
  return out;
}

const chatOpen = ref(false);
const chatInput = ref("");
const unreadCount = ref(0);
function getSystemPrompt() {
  return `Eres un asistente para Tranquero. Hoy es ${new Date().toLocaleString(
    "es-MX"
  )}. Si el usuario quiere hacer un pedido, responde en JSON con {\\"items\\":[{\\"name\\":\\"NOMBRE\\",\\"quantity\\":1}],\\"customer_id\\":null}. Si pide listar, responde con texto natural anunciando que mostrarás productos. Para preguntas generales (menú, facturas, soporte), responde en texto natural claro. Nunca muestres JSON al usuario; solo úsalo para pedidos.`;
}
const chatMessages = ref([{ role: "system", content: getSystemPrompt() }]);
const sending = ref(false);
const recording = ref(false);
const voiceLevel = ref(0);
let recognition = null;
let audioCtx = null,
  analyser = null,
  mediaStream = null,
  rafId = null,
  dataArray = null;
const speechSupported =
  typeof window !== "undefined" &&
  ("webkitSpeechRecognition" in window || "SpeechRecognition" in window);
function setupRecognition() {
  if (!speechSupported) return;
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  recognition = new Ctor();
  recognition.lang = "es-ES";
  recognition.interimResults = true;
  recognition.continuous = true;
  recognition.onresult = (e) => {
    let txt = "";
    let final = false;
    for (let i = e.resultIndex; i < e.results.length; i++) {
      txt += e.results[i][0].transcript;
      if (e.results[i].isFinal) final = true;
    }
    const out = txt.trim();
    if (!out) return;
    chatInput.value = out;
    if (final) sendVoiceCommand(out);
  };
  recognition.onend = () => {
    recording.value = false;
    stopAudioAnalyzer();
  };
  recognition.onerror = () => {
    recording.value = false;
    stopAudioAnalyzer();
  };
}
async function setupAudioAnalyzer() {
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    const source = audioCtx.createMediaStreamSource(mediaStream);
    source.connect(analyser);
    dataArray = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      voiceLevel.value = Math.min(1, rms * 2);
      rafId = requestAnimationFrame(tick);
    };
    tick();
  } catch { }
}
function stopAudioAnalyzer() {
  try {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
  } catch { }
  try {
    if (audioCtx) audioCtx.close();
  } catch { }
  try {
    mediaStream?.getTracks()?.forEach((t) => t.stop());
  } catch { }
  audioCtx = null;
  analyser = null;
  mediaStream = null;
  dataArray = null;
  voiceLevel.value = 0;
}
async function toggleMic() {
  if (!speechSupported) return;
  if (!recording.value) {
    if (!recognition) setupRecognition();
    try {
      await setupAudioAnalyzer();
      recognition.start();
      recording.value = true;
    } catch { }
  } else {
    try {
      recognition.stop();
    } catch { }
    stopAudioAnalyzer();
    recording.value = false;
  }
}
function closeChat() {
  chatOpen.value = false;
  chatInput.value = "";
  hideSuggestions();
  try {
    if (recording.value && recognition) {
      recognition.stop();
    }
  } catch { }
  stopAudioAnalyzer();
  sending.value = false;
  if (!chatMessages.value.length) {
    chatMessages.value = [{ role: "system", content: getSystemPrompt() }];
  }
  if (chatMessages.value.filter((m) => m.role !== "system").length === 0) {
    unreadCount.value = 1;
    chatMessages.value.push({
      role: "assistant",
      content: "Hola, ¿en qué puedo ayudarte hoy?",
    });
  }
}
watch(chatOpen, (v) => {
  if (!v) {
    chatInput.value = "";
    hideSuggestions();
    try {
      if (recording.value && recognition) {
        recognition.stop();
      }
    } catch { }
    stopAudioAnalyzer();
    sending.value = false;
    if (!chatMessages.value.length) {
      chatMessages.value = [{ role: "system", content: getSystemPrompt() }];
    }
    if (chatMessages.value.filter((m) => m.role !== "system").length === 0) {
      unreadCount.value = 1;
      chatMessages.value.push({
        role: "assistant",
        content: "Hola, ¿en qué puedo ayudarte hoy?",
      });
    }
  } else {
    unreadCount.value = 0;
  }
});
async function sendVoiceCommand(transcript) {
  const text = String(transcript || "").trim();
  if (!text) return;
  chatInput.value = text;
  await sendChat();
}
const chatBody = ref(null);
const quickItems = ref([]);
const displayMessages = computed(() =>
  chatMessages.value.filter((m) => m.role !== "system")
);
const user = ref(currentUser?.());
function initials(u) {
  const base = (u?.full_name || u?.username || "U").trim();
  const parts = base.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "U";
  const second = parts.length > 1 ? parts[1]?.[0] || "" : "";
  return (first + second).toUpperCase();
}
if (typeof window !== "undefined") {
  window.addEventListener("session-updated", () => {
    user.value = currentUser?.();
  });
}
onMounted(() => {
  if (chatMessages.value.filter((m) => m.role !== "system").length === 0) {
    unreadCount.value = 1;
    chatMessages.value.push({
      role: "assistant",
      content: "Hola, ¿en qué puedo ayudarte hoy?",
    });
  }
});
const mistralUrl =
  import.meta.env.VITE_MISTRAL_API_URL || import.meta.env.MISTRAL_API_URL;
const mistralKey =
  import.meta.env.VITE_MISTRAL_API_KEY || import.meta.env.MISTRAL_API_KEY;
const defaultModel =
  import.meta.env.VITE_MISTRAL_MODEL ||
  import.meta.env.MISTRAL_MODEL ||
  "mistral-small-latest";
const lastItemName = ref("");

async function forceSyncLatestCart() {
  try {
    const u = currentUser?.();
    if (!u?.id) {
      chatMessages.value.push({
        role: "assistant",
        content: "Inicia sesión para gestionar tu carrito.",
      });
      return;
    }
    const { data: carts } = await supabase
      .from("carts")
      .select("id_carts")
      .eq("status", "active")
      .eq("user_id", u.id)
      .order("created_at", { descending: true })
      .limit(1);
    const cid = carts?.[0]?.id_carts;
    if (!cid) {
      cartState.items = [];
      return;
    }
    const { data: rows } = await supabase
      .from("cart_items")
      .select("product_id, quantity, unit_price")
      .eq("cart_id", cid);
    const ids = (rows || []).map((r) => r.product_id);
    let map = new Map();
    if (ids.length) {
      const { data: details } = await supabase
        .from("v_inventory_basic")
        .select("id,name,image_base64,image_mime")
        .in("id", ids);
      (details || []).forEach((d) => map.set(d.id, d));
    }
    cartState.items = (rows || []).map((r) => {
      const d = map.get(r.product_id) || {};
      return {
        id: r.product_id,
        name: d.name || "",
        unit_price: Number(r.unit_price || 0),
        qty: Number(r.quantity || 1),
        image_base64: d.image_base64 || null,
        image_mime: d.image_mime || null,
      };
    });
  } catch {
    cartState.items = [];
  }
}

async function getActiveCartIds() {
  const u = currentUser?.();
  if (!u?.id) return [];
  const { data } = await supabase
    .from("carts")
    .select("id_carts")
    .eq("status", "active")
    .eq("user_id", u.id)
    .order("created_at", { descending: true });
  return (data || []).map((c) => c.id_carts);
}

function scrollDown() {
  const el = chatBody.value;
  if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
}
function usePrompt(p) {
  chatInput.value = p;
  sendChat();
}

async function loadProductSuggestions(page = 0) {
  const pageSize = 20;
  const start = page * pageSize;
  const end = start + pageSize - 1;
  const { data } = await supabase
    .from("v_inventory_basic")
    .select("id,name,unit_price,discount,image_base64,image_mime")
    .range(start, end);
  const rows = data || [];
  if (page === 0) quickItems.value = [];
  quickItems.value = [
    ...quickItems.value,
    ...rows.map((r) => ({
      id: r.id,
      name: r.name,
      price: finalPrice(r.unit_price, r.discount),
      image_base64: r.image_base64,
      image_mime: r.image_mime,
    })),
  ];
  suggestHasMore.value = rows.length === pageSize;
}
const suggestVisible = ref(false);
const suggestPage = ref(0);
const suggestHasMore = ref(false);
const suggestEl = ref(null);
function showSuggestions() {
  suggestVisible.value = true;
  suggestPage.value = 0;
  chatMessages.value.push({
    role: "assistant",
    content: "Selecciona un producto de la lista para añadir al carrito.",
  });
  loadProductSuggestions(0);
  scrollDown();
}
function hideSuggestions() {
  suggestVisible.value = false;
  quickItems.value = [];
}
function loadMoreSuggestions() {
  suggestPage.value += 1;
  loadProductSuggestions(suggestPage.value);
}
function onSuggestScroll() {
  const el = suggestEl.value;
  if (!el) return;
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
  if (nearBottom && suggestHasMore.value) loadMoreSuggestions();
}
const selectedQty = ref({});
function getQty(id) {
  return Math.max(1, Number(selectedQty.value[id] || 1));
}
function incQty(id) {
  selectedQty.value[id] = getQty(id) + 1;
}
function decQty(id) {
  selectedQty.value[id] = Math.max(1, getQty(id) - 1);
}
function useQuickItem(item) {
  addOrderToCart({
    items: [{ product_id: item.id, quantity: getQty(item.id) }],
    customer_id: null,
  });
}
function shouldListIntent(q) {
  const s = (q || "").toLowerCase();
  return (
    s.includes("listar") ||
    s.includes("lista") ||
    s.includes("productos") ||
    s.includes("ofrecen") ||
    s.includes("ver productos") ||
    s.includes("catalogo") ||
    s.includes("catálogo") ||
    s.includes("menu") ||
    s.includes("menú")
  );
}
function shouldTotalIntent(q) {
  const s = (q || "").toLowerCase();
  return (
    s.includes("total") ||
    (/(cuanto|cuánto)/.test(s) &&
      (s.includes("carrito") ||
        s.includes("pedido") ||
        s.includes("importe") ||
        s.includes("saldo")))
  );
}
async function authoritativeCartTotal() {
  try {
    if (useSupabase()) {
      const ids = await getActiveCartIds();
      const cid = ids[0] || null;
      if (cid) {
        const { data } = await supabase
          .from("v_cart_totals")
          .select("total, items_count")
          .eq("cart_id", cid)
          .limit(1);
        const row = (data || [])[0];
        if (row) return Number(row.total || 0);
      }
    }
  } catch { }
  return cartTotal();
}

function parseAssistantPayload(text) {
  try {
    const match = text.match(/\{[\s\S]*\}/);
    if (!match) return null;
    const obj = JSON.parse(match[0]);
    if (typeof obj.response === "string" && obj.response.trim())
      return { response: obj.response.trim() };
    if (Array.isArray(obj.items)) {
      const items = obj.items.filter(
        (it) =>
          (it.sku && it.sku.trim()) ||
          it.product_id ||
          (it.name && it.name.trim())
      );
      if (!items.length) return null;
      return { order: { items, customer_id: obj.customer_id ?? null } };
    }
    return null;
  } catch {
    return null;
  }
}

function normalizeText(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
const numberWords = {
  un: 1,
  uno: 1,
  una: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
  diez: 10,
  doce: 12,
  docena: 12,
};
function extractQuantity(text) {
  const lower = normalizeText(text);
  if (/\bmedia\s+docena\b/.test(lower)) return 6;
  if (/\bdocena\b/.test(lower)) return 12;
  if (/\bun\s+par\b/.test(lower) || /\bpar\b/.test(lower)) return 2;
  const m = String(text || "").match(/\b(\d+)\b/);
  if (m) return Number(m[1]);
  const words = lower.split(" ");
  for (const w of words) {
    if (numberWords[w] !== undefined) return numberWords[w];
  }
  return 1;
}
function toSingular(w) {
  if (!w) return w;
  if (w.endsWith("ces") && w.length > 4) return w.slice(0, -3) + "z";
  if (w.endsWith("es") && w.length > 4) return w.slice(0, -2);
  if (w.endsWith("s") && w.length > 3) return w.slice(0, -1);
  return w;
}
function tokenize(s) {
  const stop = new Set([
    "me",
    "por",
    "favor",
    "porfa",
    "de",
    "del",
    "la",
    "el",
    "los",
    "las",
    "al",
    "a",
    "un",
    "una",
    "uno",
    "unos",
    "unas",
    "dos",
    "tres",
    "orden",
    "ordenes",
    "ord",
    "quiero",
    "agrega",
    "añade",
    "pon",
    "suma",
    "compra",
    "compras",
    "pedido",
    "pedidos",
    "carrito",
    "carro",
    "cesta",
    "canasta",
    "basket",
    "otro",
    "otra",
    "otros",
    "mas",
    "más",
  ]);
  const arr = normalizeText(s)
    .split(" ")
    .filter(Boolean)
    .map(toSingular)
    .filter((w) => !stop.has(w))
    .filter((w) => isNaN(Number(w)))
    .filter((w) => w.length >= 2);
  return Array.from(new Set(arr));
}
async function resolveProductByText(term) {
  const t = String(term || "").trim();
  if (!t) return null;
  const tokens = tokenize(t);
  let qb = supabase
    .from("v_inventory_basic")
    .select("id,unit_price,discount,name,stock_qty,tax_rate")
    .limit(1);
  for (const w of tokens) {
    qb = qb.ilike("name", `%${w}%`);
  }
  const andRes = await qb;
  const hit = (andRes.data || [])[0];
  if (hit)
    return {
      pid: hit.id,
      name: hit.name,
      price: finalPrice(hit.unit_price, hit.discount, hit.tax_rate),
      stock: hit.stock_qty,
    };
  const direct = await supabase
    .from("v_inventory_basic")
    .select("id,unit_price,discount,name,stock_qty,tax_rate")
    .ilike("name", `%${t}%`)
    .limit(1);
  const r = (direct.data || [])[0];
  if (r)
    return {
      pid: r.id,
      name: r.name,
      price: finalPrice(r.unit_price, r.discount, r.tax_rate),
      stock: r.stock_qty,
    };
  const { data: all } = await supabase
    .from("v_inventory_basic")
    .select("id,unit_price,discount,name,stock_qty,tax_rate")
    .limit(300);
  const qTokens = tokens;
  let best = null;
  let bestScore = 0;
  (all || []).forEach((p) => {
    const pTokens = tokenize(p.name);
    const score = qTokens.reduce(
      (s, w) => s + (pTokens.includes(w) ? 1 : 0),
      0
    );
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  });
  if (best && bestScore > 0)
    return {
      pid: best.id,
      name: best.name,
      price: finalPrice(best.unit_price, best.discount, best.tax_rate),
      stock: best.stock_qty,
    };
  return null;
}
function productNameFromPart(p) {
  const toks = tokenize(p);
  return toks.join(" ").trim();
}
async function localOrderFromText(text) {
  const raw = String(text || "");
  const s = raw.toLowerCase();
  if (!/(agrega|añade|pon|suma|compra|quiero)/.test(s)) return null;
  const parts = raw
    .split(/,| y /gi)
    .map((p) => p.trim())
    .filter(Boolean);
  const items = [];
  for (const p of parts) {
    const qty = extractQuantity(p);
    let name = productNameFromPart(p);
    if (
      !name &&
      /\b(otro|otra|otros|mas|más)\b/i.test(normalizeText(p)) &&
      lastItemName.value
    )
      name = lastItemName.value;
    if (!name) continue;
    const prod = await resolveProductByText(name);
    if (prod) {
      items.push({ product_id: prod.pid, quantity: qty, name: prod.name, unit_price: prod.price, stock: prod.stock });
      lastItemName.value = prod.name;
    }
  }
  return items.length ? { items, customer_id: null } : null;
}

async function localRemoveFromText(text) {
  const raw = String(text || "");
  const s = normalizeText(raw);
  if (
    !/(quita|quitar|elimina|eliminar|borra|borrar|remueve|saca|vaciar|vacia|limpia|limpiar)/.test(
      s
    )
  )
    return null;
  if (
    /vaciar|vac\s*i\s*a\s*r|limpia(r)?|borra(r)?\s+todo|quitar\s+todo|eliminar\s+todo|vaciar\s+carrito|vaciar\s+el\s+carrito|vacia\s+carrito|vacia\s+el\s+carrito|vaciar\s+todo|vacia\s+todo|vaciar\s+(carro|cesta|canasta|basket)|vacia\s+(carro|cesta|canasta|basket)|limpiar\s+(carro|cesta|canasta|basket)/.test(
      s
    )
  )
    return { clear: true };
  const parts = raw
    .split(/,| y /gi)
    .map((p) => p.trim())
    .filter(Boolean);
  const items = [];
  for (const p of parts) {
    const qty = extractQuantity(p);
    let name = productNameFromPart(p);
    if (
      !name &&
      /\b(otro|otra|otros|mas|más)\b/i.test(normalizeText(p)) &&
      lastItemName.value
    )
      name = lastItemName.value;
    if (!name) continue;
    const prod = await resolveProductByText(name);
    if (prod)
      items.push({ product_id: prod.pid, quantity: qty, name: prod.name });
  }
  return items.length ? { items } : null;
}

async function removeItemsFromCart(spec) {
  const usingSB = !!useSupabase();
  const u = currentUser?.();
  if (usingSB && !u?.id) {
    chatMessages.value.push({
      role: "assistant",
      content: "Debes iniciar sesión para modificar tu carrito.",
    });
    return;
  }
  const carts = usingSB ? await getActiveCartIds() : [];
  if (spec.clear) {
    const prev = cartState.items
      .slice()
      .map((it) => ({ product_id: it.id, quantity: it.qty }));
    let thumbs = [];
    if (usingSB) {
      if (carts.length)
        await supabase.from("cart_items").delete().in("cart_id", carts);
      await forceSyncLatestCart();
      thumbs = await buildThumbs(prev);
    } else {
      await clearCart();
      thumbs = prev.map((p) => {
        const d = {
          name:
            cartState.items.find((i) => i.id === p.product_id)?.name ||
            "Producto",
          price: 0,
        };
        return {
          name: d.name,
          qty: Number(p.quantity || 1),
          price: 0,
          image_base64: null,
          image_mime: null,
        };
      });
    }
    chatMessages.value.push({ role: "assistant", content: "Carrito vaciado." });
    if (thumbs.length)
      chatMessages.value.push({
        role: "assistant",
        type: "thumbs",
        items: thumbs,
      });
    const totalNow = await authoritativeCartTotal();
    chatMessages.value.push({
      role: "assistant",
      content: "Total actual: " + formatMoney(totalNow),
    });
    return;
  }
  const removed = [];
  let thumbsRemoved = [];
  if (usingSB) {
    thumbsRemoved = await buildThumbs(
      (spec.items || []).map((it) => ({
        product_id: it.product_id,
        quantity: it.quantity,
      }))
    );
    for (const it of spec.items || []) {
      const q = Number(it.quantity || 1);
      if (!carts.length) continue;
      const { data: rows } = await supabase
        .from("cart_items")
        .select("id_cart_items, cart_id, quantity")
        .in("cart_id", carts)
        .eq("product_id", it.product_id)
        .order("added_at", { descending: true })
        .limit(1);
      const row = (rows || [])[0];
      if (!row) continue;
      const newQty = Math.max(0, Number(row.quantity || 0) - q);
      if (newQty <= 0) {
        await supabase
          .from("cart_items")
          .delete()
          .eq("id_cart_items", row.id_cart_items);
      } else {
        await supabase
          .from("cart_items")
          .update({ quantity: newQty })
          .eq("id_cart_items", row.id_cart_items);
      }
      removed.push({
        name:
          cartState.items.find((i) => i.id === it.product_id)?.name ||
          "Producto",
        qty: q,
      });
    }
    await forceSyncLatestCart();
  } else {
    for (const it of spec.items || []) {
      const q = Number(it.quantity || 1);
      const cur = cartState.items.find((i) => i.id === it.product_id);
      if (!cur) continue;
      const newQty = Math.max(0, Number(cur.qty || 0) - q);
      if (newQty <= 0) await removeFromCart(it.product_id);
      else await setItemQty(it.product_id, newQty);
      removed.push({ name: cur.name || "Producto", qty: q });
      thumbsRemoved.push({
        name: cur.name || "Producto",
        qty: q,
        price: Number(cur.unit_price || 0),
        image_base64: cur.image_base64 || null,
        image_mime: cur.image_mime || null,
      });
    }
  }
  if (removed.length)
    chatMessages.value.push({
      role: "assistant",
      content:
        "Quitado: " + removed.map((r) => `${r.qty}x ${r.name}`).join(", "),
    });
  if (thumbsRemoved.length)
    chatMessages.value.push({
      role: "assistant",
      type: "thumbs",
      items: thumbsRemoved,
    });
  const totalNow = await authoritativeCartTotal();
  chatMessages.value.push({
    role: "assistant",
    content: "Total actual: " + formatMoney(totalNow),
  });
}

async function addOrderToCart(order) {
  const u = currentUser?.();
  if (!u?.id) {
    chatMessages.value.push({
      role: "assistant",
      content: "Debes iniciar sesión para crear y actualizar tu carrito.",
    });
    return;
  }

  // Always resolve items manually to validate stock
  try {
    const resolved = [];
    for (const it of order.items || []) {
      let pid = it.product_id || null;
      let unit = it.unit_price || null;
      let name = it.name || null;
      let stock = it.stock !== undefined ? it.stock : null;

      if (!pid || !unit || !name || stock === null) {
        if (it.product_id) {
          const { data: prow } = await supabase
            .from("v_inventory_basic")
            .select("id,unit_price,discount,name,stock_qty,tax_rate")
            .eq("id", it.product_id)
            .limit(1);
          pid = pid || prow?.[0]?.id;
          unit = unit || finalPrice(prow?.[0]?.unit_price, prow?.[0]?.discount, prow?.[0]?.tax_rate);
          name = name || prow?.[0]?.name;
          stock = prow?.[0]?.stock_qty;
        } else if (it.sku) {
          const { data: prow } = await supabase
            .from("v_inventory_basic")
            .select("id,unit_price,discount,name,stock_qty,tax_rate")
            .eq("sku", it.sku)
            .limit(1);
          pid = pid || prow?.[0]?.id;
          unit = unit || finalPrice(prow?.[0]?.unit_price, prow?.[0]?.discount, prow?.[0]?.tax_rate);
          name = name || prow?.[0]?.name;
          stock = prow?.[0]?.stock_qty;
        } else if (it.name) {
          const { data: prow } = await supabase
            .from("v_inventory_basic")
            .select("id,unit_price,discount,name,stock_qty,tax_rate")
            .ilike("name", it.name)
            .limit(1);
          pid = pid || prow?.[0]?.id;
          unit = unit || finalPrice(prow?.[0]?.unit_price, prow?.[0]?.discount, prow?.[0]?.tax_rate);
          name = name || prow?.[0]?.name;
          stock = prow?.[0]?.stock_qty;
        }
      }

      if (!pid) continue;
      const qty = Number(it.quantity || 1);
      const price = Number(unit || 0);
      const available = Number(stock || 0);

      if (qty > available) {
        chatMessages.value.push({
          role: "assistant",
          content: `No hay suficiente stock de ${name || "Producto"
            }. Solo quedan ${available} disponibles.`,
        });
        continue; // Skip adding this item
      }

      resolved.push({ pid, qty, price, name });
      lastItemName.value = name || lastItemName.value;
    }

    if (!resolved.length && (order.items || []).length > 0) {
      // If we had items but none were resolved (or all failed stock check), we stop here.
      // Messages were already pushed for stock failures.
      // If it was just resolution failure:
      const hadStockFailures = chatMessages.value.some((m) =>
        m.content.includes("No hay suficiente stock")
      );
      if (!hadStockFailures) {
        chatMessages.value.push({
          role: "assistant",
          content:
            "No se pudo crear el carrito: el pedido no contiene productos reconocibles. Indica nombre o product_id.",
        });
        showSuggestions();
      }
      return;
    }

    for (const r of resolved) {
      await addToCart(
        { id: r.pid, name: r.name || "Producto", unit_price: r.price },
        r.qty
      );
    }
    await forceSyncLatestCart();
    chatMessages.value.push({
      role: "assistant",
      content: `Carrito sincronizado. Resumen: ${resolved
        .map((r) => `${r.qty}x ${r.name || "Producto"}`)
        .join(", ")}`,
    });
    const thumbsItems = await buildThumbs(
      resolved.map((r) => ({ product_id: r.pid, quantity: r.qty }))
    );
    if (thumbsItems.length)
      chatMessages.value.push({
        role: "assistant",
        type: "thumbs",
        items: thumbsItems,
      });
    const totalNow = await authoritativeCartTotal();
    chatMessages.value.push({
      role: "assistant",
      content: "Total actual: " + formatMoney(totalNow),
    });
  } catch (e) {
    chatMessages.value.push({
      role: "assistant",
      content: "Error al crear carrito: " + (e?.message || ""),
    });
  }
}

async function sendChat() {
  const text = chatInput.value.trim();
  if (!text) return;
  if (recording.value && recognition) {
    try {
      recognition.stop();
    } catch { }
    recording.value = false;
  }
  chatInput.value = "";
  sending.value = true;
  const userMsg = { role: "user", content: text };
  chatMessages.value.push(userMsg);
  scrollDown();
  const q = text.toLowerCase();
  if (shouldTotalIntent(q)) {
    await forceSyncLatestCart();
    const totalNow = await authoritativeCartTotal();
    chatMessages.value.push({
      role: "assistant",
      content: "Total actual: " + formatMoney(totalNow),
    });
    scrollDown();
    sending.value = false;
    return;
  }
  if (shouldListIntent(q)) {
    showSuggestions();
    const listMsg = "Productos disponibles: selecciona de la lista inferior.";
    chatMessages.value.push({ role: "assistant", content: listMsg });
    scrollDown();
    sending.value = false;
    return;
  }
  const rm = await localRemoveFromText(text);
  if (rm) {
    await removeItemsFromCart(rm);
    hideSuggestions();
    scrollDown();
    sending.value = false;
    return;
  }
  const local = await localOrderFromText(text);
  if (local && local.items && local.items.length) {
    await addOrderToCart(local);
    hideSuggestions();
    chatMessages.value.push({
      role: "assistant",
      content: `Añadido: ${local.items
        .map((i) => `${i.quantity}x ${i.name || ""}`)
        .join(", ")}`,
    });
    scrollDown();
    sending.value = false;
    return;
  }
  chatInput.value = "";
  sending.value = true;
  try {
    // Verificar que las variables estén configuradas
    let baseUrl = import.meta.env.VITE_MISTRAL_API_URL || import.meta.env.MISTRAL_API_URL;
    const key = import.meta.env.VITE_MISTRAL_API_KEY || import.meta.env.MISTRAL_API_KEY;

    if (!baseUrl || !key) {
      chatMessages.value.push({
        role: "assistant",
        content: "Error: Las variables de IA no están configuradas. Verifica VITE_MISTRAL_API_URL y VITE_MISTRAL_API_KEY en producción.",
      });
      scrollDown();
      sending.value = false;
      return;
    }

    // Asegurar que la URL incluya el path completo de la API
    // Si solo tiene la base URL, agregar el path
    if (!baseUrl.includes('/v1/chat/completions')) {
      baseUrl = baseUrl.replace(/\/$/, '') + '/v1/chat/completions';
    }

    const url = baseUrl;
    const candidates = Array.from(
      new Set([
        defaultModel,
        "mistral-small-latest",
        "mistral-tiny",
        "mistral-medium-latest",
      ])
    );
    let reply = "";
    for (const mdl of candidates) {
      const payload = {
        model: mdl,
        messages: chatMessages.value.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        temperature: 0.2,
        max_tokens: 256,
      };
      console.log('[IA Chat] Enviando request a:', url);
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${key}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        console.error('[IA Chat] Error en respuesta:', res.status, errorData);
        throw new Error(errorData?.error?.message || `Error ${res.status}`);
      }

      const data = await res.json();
      if (res.ok) {
        reply = (data?.choices?.[0]?.message?.content || "").trim();
        break;
      }
      const msg = (data?.error || data?.message || "").toLowerCase();
      if (
        !(
          res.status === 429 ||
          msg.includes("capacity") ||
          msg.includes("tier")
        )
      ) {
        chatMessages.value.push({
          role: "assistant",
          content: data?.error || data?.message || "Error al contactar la IA.",
        });
        sending.value = false;
        scrollDown();
        return;
      }
    }
    const parsed = parseAssistantPayload(reply);
    if (parsed?.order) {
      await addOrderToCart(parsed.order);
    }
    if (parsed?.response) {
      const r = parsed.response.toLowerCase();
      if (shouldListIntent(r)) {
        showSuggestions();
      } else {
        chatMessages.value.push({
          role: "assistant",
          content: parsed.response,
        });
        hideSuggestions();
      }
    }
    if (!parsed) {
      chatMessages.value.push({ role: "assistant", content: reply || "..." });
      hideSuggestions();
    }
    scrollDown();
  } catch (err) {
    console.error('Error en IA:', err);
    const errorMsg = err?.message || 'Error desconocido';
    chatMessages.value.push({
      role: "assistant",
      content: `Error al contactar la IA: ${errorMsg}. Verifica que VITE_MISTRAL_API_URL y VITE_MISTRAL_API_KEY estén configuradas.`,
    });
    scrollDown();
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <button class="chat-fab" :class="{ 'has-unread': !chatOpen && unreadCount > 0 }" @click="chatOpen = !chatOpen"
    aria-label="Abrir chat">
    <img :src="chefImg" class="fab-img" alt="Asistente" />
    <span v-if="!chatOpen && unreadCount > 0" class="fab-badge">{{
      unreadCount
    }}</span>
  </button>
  <div v-if="chatOpen" class="chat-panel">
    <div class="chat-header">
      <div class="brand">
        <img :src="chefImg" class="avatar-img" alt="Asistente" />
        <div class="title">Chefcito</div>
        <div class="sub">Respuestas rápidas</div>
      </div>
      <button class="chip icon-only" @click="closeChat" aria-label="Cerrar">
        <span class="material-symbols-rounded" aria-hidden="true">close</span>
      </button>
    </div>
    <div class="chat-body" ref="chatBody">
      <div v-for="(m, idx) in displayMessages" :key="idx" :class="['msg-line', m.role]">
        <template v-if="m.role === 'assistant'">
          <img :src="chefImg" class="bubble-avatar" alt="Asistente" />
          <div :class="['msg', 'assistant']">
            <template v-if="m.type === 'thumbs'">
              <div class="thumbs-list">
                <div v-for="it in m.items" :key="it.name + it.qty" class="thumb-row">
                  <img v-if="it.image_base64" :src="`data:${it.image_mime || 'image/jpeg'};base64,${it.image_base64
                    }`" class="thumb-img" alt="" />
                  <div class="thumb-info">
                    <b>{{ it.qty }}x {{ it.name }}</b>
                    <span class="thumb-price">{{ formatMoney(it.price) }}</span>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>{{ m.content }}</template>
          </div>
        </template>
        <template v-else>
          <div :class="['msg', 'user']">
            <template v-if="m.type === 'thumbs'">
              <div class="thumbs-list">
                <div v-for="it in m.items" :key="it.name + it.qty" class="thumb-row">
                  <img v-if="it.image_base64" :src="`data:${it.image_mime || 'image/jpeg'};base64,${it.image_base64
                    }`" class="thumb-img" alt="" />
                  <div class="thumb-info">
                    <b>{{ it.qty }}x {{ it.name }}</b>
                    <span class="thumb-price">{{ formatMoney(it.price) }}</span>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>{{ m.content }}</template>
          </div>
          <img v-if="user?.avatar_url" :src="user.avatar_url" class="bubble-avatar" alt="Tú" />
          <span v-else class="bubble-avatar fallback">{{
            initials(user)
          }}</span>
        </template>
      </div>
      <div v-if="sending" class="msg-line assistant">
        <img :src="chefImg" class="bubble-avatar" alt="Asistente" />
        <div class="msg assistant typing">
          <span class="dot"></span><span class="dot"></span><span class="dot"></span>
        </div>
      </div>
    </div>
    <div v-if="suggestVisible" class="chat-suggestions" ref="suggestEl" @scroll.passive="onSuggestScroll">
      <div class="sg-head">
        <b>Productos</b>
        <div class="tools">
          <button class="chip" @click="hideSuggestions">Ocultar</button>
          <button v-if="suggestHasMore" class="chip" @click="loadMoreSuggestions">
            Ver más
          </button>
        </div>
      </div>
      <div v-for="it in quickItems" :key="it.id" class="sg-item">
        <img v-if="it.image_base64" :src="`data:${it.image_mime || 'image/jpeg'};base64,${it.image_base64
          }`" alt="" class="sg-thumb" />
        <div class="sg-info">
          <div class="sg-name">{{ it.name }}</div>
          <div class="sg-price">{{ formatMoney(it.price) }}</div>
        </div>
        <div class="sg-qty">
          <button class="qty-btn" @click="decQty(it.id)">-</button>
          <span class="qty-val">{{ getQty(it.id) }}</span>
          <button class="qty-btn" @click="incQty(it.id)">+</button>
        </div>
        <button class="sg-add" @click="useQuickItem(it)">
          <span class="material-symbols-rounded">shopping_bag</span>
          Agregar
        </button>
      </div>
    </div>
    <div class="tools-bar">
      <button class="chip" @click="showSuggestions">Mostrar productos</button>
      <button class="chip faq" @click="usePrompt('¿Cuál es el total del carrito?')">
        Total del carrito
      </button>
      <button class="chip faq" @click="usePrompt('¿Tiempo de entrega estimado?')">
        Tiempo de entrega
      </button>
      <button class="chip" v-if="suggestVisible" @click="hideSuggestions">
        Ocultar productos
      </button>
    </div>
    <div class="chat-input">
      <input v-model="chatInput" :disabled="sending" placeholder="Escribe y presiona Enter..."
        @keydown.enter.exact.prevent="sendChat" />
      <button class="mic-btn" :class="{ active: recording }" @click="toggleMic" :disabled="!speechSupported || sending"
        aria-label="Micrófono">
        <span class="material-symbols-rounded">{{
          recording ? "mic" : "mic_off"
        }}</span>
        <span v-if="recording" class="mic-waves">
          <span class="w w1" :style="{ height: 8 + voiceLevel * 24 + 'px' }"></span>
          <span class="w w2" :style="{ height: 10 + voiceLevel * 32 + 'px' }"></span>
          <span class="w w3" :style="{ height: 8 + voiceLevel * 28 + 'px' }"></span>
        </span>
      </button>
      <button class="send-btn" @click="sendChat" :disabled="sending || !chatInput.trim()">
        <span class="material-symbols-rounded">send</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.chat-fab {
  /* Define variables locally to ensure they exist */
  --bg: #ffffff;
  --surface: #ffffff;
  --text: #0f172a;
  --border: #e2e8f0;
  --muted: #64748b;

  position: fixed;
  bottom: 22px;
  right: 22px;
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6366f1, #14b8a6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  box-shadow: 0 16px 36px rgba(2, 6, 23, 0.25);
  cursor: pointer;
  z-index: 50;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  color-scheme: light dark;
}

.chat-fab.has-unread::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 999px;
  box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45);
  animation: fabPulse 2s ease-out infinite;
}

@keyframes fabPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45);
  }

  70% {
    box-shadow: 0 0 0 14px rgba(239, 68, 68, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

.fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ef4444;
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.35);
}

.chat-fab:hover {
  transform: translateY(-1px);
  box-shadow: 0 22px 44px rgba(2, 6, 23, 0.28);
}

.chat-panel {
  /* Define variables locally to ensure they exist and background is opaque */
  --bg: #ffffff;
  --surface: #ffffff;
  --text: #0f172a;
  --border: #e2e8f0;
  --muted: #64748b;

  position: fixed;
  bottom: 108px;
  right: 22px;
  width: 420px;
  max-height: 72vh;
  background: var(--surface);
  /* Now guaranteed to be defined */
  border-radius: 26px;
  box-shadow: 0 24px 56px rgba(38, 45, 80, 0.28);
  display: grid;
  grid-template-rows: auto 1fr auto auto;
  z-index: 51;
  overflow: hidden;
  border: 2px solid transparent;
  background-clip: padding-box;
  color-scheme: light dark;
}

.chat-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 26px;
  padding: 2px;
  background: linear-gradient(135deg, #6a5cf5, #00c2ff);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  background: linear-gradient(135deg, #6a5cf5, #00c2ff);
  color: #fff;
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.avatar-img {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  object-fit: cover;
}

.fab-img {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  object-fit: cover;
}

.title {
  font-weight: 800;
  color: #fff;
}

.sub {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  margin-left: 0.35rem;
}

.chat-body {
  padding: 0.9rem;
  overflow: auto;
  display: grid;
  gap: 0.7rem;
  background: var(--surface);
}

.thumbs-list {
  display: grid;
  gap: 0.4rem;
}

.thumbs-list .thumb-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.thumb-img {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
}

.thumb-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.thumb-info b {
  flex: 1 1 auto;
  color: var(--text);
}

.thumb-price {
  color: var(--text);
  font-weight: 700;
  margin-left: auto;
  white-space: nowrap;
}

.msg {
  padding: 0.62rem 0.8rem;
  border-radius: 18px;
  font-size: 0.95rem;
  max-width: 84%;
  line-height: 1.35;
}

.msg.user {
  background: linear-gradient(135deg, #6a5cf5, #00c2ff);
  color: #fff;
  justify-self: end;
  box-shadow: 0 12px 24px rgba(106, 92, 245, 0.25);
}

.msg.assistant {
  background: var(--bg);
  border: 1px solid var(--border);
  color: var(--text);
  justify-self: start;
  text-align: justify;
  text-justify: inter-word;
  word-break: break-word;
}

.msg.assistant.typing {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.msg-line {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.msg-line.assistant {
  justify-content: flex-start;
}

.msg-line.user {
  justify-content: flex-end;
}

.bubble-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  object-fit: cover;
  flex: 0 0 28px;
}

.bubble-avatar.fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(124, 92, 255, 0.14);
  color: var(--text);
  font-weight: 800;
}

.typing .dot {
  width: 6px;
  height: 6px;
  background: var(--text);
  border-radius: 50%;
  animation: pulse 1.2s infinite ease-in-out;
  opacity: 0.6;
}

.typing .dot:nth-child(2) {
  animation-delay: 0.15s;
}

.typing .dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes pulse {

  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.4;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.chat-suggestions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.6rem;
  padding: 0 1rem 0.8rem;
  max-height: 42vh;
  overflow: auto;
}

.sg-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  background: var(--surface);
  padding: 0.4rem 0;
  color: var(--text);
}

.sg-item {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 0.6rem;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 16px;
  padding: 0.55rem 0.7rem;
  box-shadow: 0 6px 16px rgba(38, 45, 80, 0.08);
}

.sg-thumb {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  object-fit: cover;
}

.sg-info {
  display: grid;
  gap: 0.15rem;
}

.sg-name {
  font-weight: 700;
  color: var(--text);
}

.sg-price {
  color: var(--text);
  font-weight: 700;
}

.sg-qty {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.qty-btn {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
}

.qty-val {
  min-width: 20px;
  text-align: center;
  color: var(--text);
}

.sg-add {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.7rem;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #6a5cf5, #00c2ff);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(106, 92, 245, 0.25);
}

.chat-input {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.8rem 1rem;
  border-top: 1px solid var(--border);
  background: var(--surface);
}

.chat-input input {
  flex: 1;
  padding: 0.6rem 0.9rem;
  border: 2px solid #60a5fa;
  border-radius: 999px;
  background: var(--surface);
  color: var(--text);
  outline: none;
}

.chat-input input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.25);
}

.send-btn {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: linear-gradient(135deg, #6a5cf5, #00c2ff);
  color: #fff;
  box-shadow: 0 10px 22px rgba(106, 92, 245, 0.25);
  cursor: pointer;
}

.mic-btn {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--muted);
  box-shadow: none;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease,
    border-color 160ms ease, background-color 160ms ease;
}

.mic-btn .material-symbols-rounded {
  color: inherit;
}

.mic-btn.active {
  border: none;
  background: linear-gradient(135deg, #6a5cf5, #00c2ff);
  color: #fff;
  box-shadow: 0 10px 22px rgba(106, 92, 245, 0.25);
}

.mic-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(2, 6, 23, 0.16);
  border-color: var(--border);
  background: var(--surface);
  color: var(--text);
}

.mic-btn.active:not(:disabled):hover {
  box-shadow: 0 12px 26px rgba(106, 92, 245, 0.32);
}

.mic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mic-waves {
  position: absolute;
  inset: 6px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
  pointer-events: none;
}

.mic-waves .w {
  width: 3px;
  height: 8px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.9);
  transition: height 80ms ease;
}

.tools-bar {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-top: 1px dashed var(--border);
  background: var(--bg);
}

.chat-panel .chip {
  border: 1.6px solid transparent;
  border-radius: 999px;
  padding: 0.38rem 0.7rem;
  color: #6a5cf5;
  background: linear-gradient(var(--surface), var(--surface)) padding-box,
    linear-gradient(135deg, #6a5cf5, #00c2ff) border-box;
  cursor: pointer;
}

.chat-panel .chip:hover {
  background: linear-gradient(var(--bg), var(--bg)) padding-box,
    linear-gradient(135deg, #6a5cf5, #00c2ff) border-box;
}

.chat-panel .chip.icon-only {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
}

.chat-panel .chip.icon-only .material-symbols-rounded {
  font-size: 18px;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .chat-fab {
    --bg: #0f172a;
    --surface: #1e293b;
    --text: #f8fafc;
    --border: #334155;
    --muted: #94a3b8;
  }

  .chat-panel {
    --bg: #0f172a;
    --surface: #1e293b;
    --text: #f8fafc;
    --border: #334155;
    --muted: #94a3b8;

    color-scheme: dark;
    box-shadow: 0 24px 56px rgba(0, 0, 0, 0.5);
  }

  .chat-body {
    background: var(--surface);
  }

  .chat-panel .chip {
    color: #ffffff;
  }

  .chat-panel .chip:hover {
    color: #ffffff;
  }

  .msg.assistant {
    background: var(--bg);
    border-color: var(--border);
  }

  .sg-item {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }

  .chat-input input {
    color-scheme: dark;
  }
}

/* Light mode support */
@media (prefers-color-scheme: light) {
  .chat-panel {
    color-scheme: light;
  }

  .chat-input input {
    color-scheme: light;
  }
}

:root[data-theme="dark"] .chat-panel,
:root[data-theme="dark"] .chat-fab {
  --bg: #0f172a;
  --surface: #1e293b;
  --text: #f8fafc;
  --border: #334155;
  --muted: #94a3b8;
  color-scheme: dark;
}

:root[data-theme="dark"] .chat-panel .chip {
  color: #ffffff;
}

:root[data-theme="dark"] .chat-panel .chip:hover {
  color: #ffffff;
}

:root[data-theme="light"] .chat-panel,
:root[data-theme="light"] .chat-fab {
  --bg: #ffffff;
  --surface: #ffffff;
  --text: #0f172a;
  --border: #e2e8f0;
  --muted: #64748b;
  color-scheme: light;
}

@media (max-width: 520px) {
  .chat-panel {
    right: 10px;
    width: calc(100vw - 20px);
  }
}
</style>
